import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

import { EpisodeWord } from "../../../../Episode/services/episode.service";
import {
  MIN_CLIP_LENGTH,
  TimeRange,
  centerView,
  clamp,
  findWordIndexAt,
  fitViewToClip,
  formatTimecode,
  panView,
  pickTickStep,
  snapToWordBoundary,
  speakerColor,
  zoomView,
} from "../utils";

type DragMode = "start" | "end" | "move" | "seek";

interface DragState {
  mode: DragMode;
  offset: number;
  startX: number;
  moved: boolean;
}

interface FocusTimelineProps {
  duration: number;
  view: TimeRange;
  onViewChange: (view: TimeRange) => void;
  trim: TimeRange;
  onTrimChange: (start: number, end: number) => void;
  currentTime: number;
  onSeek: (time: number) => void;
  words: EpisodeWord[];
  snapToWords: boolean;
  onSnapToWordsChange: (value: boolean) => void;
  isOverLimit: boolean;
}

const RULER_HEIGHT = 22;
const LANE_HEIGHT = 64;
const SNAP_TOLERANCE_PX = 10;
const MAX_SPAN_FOR_WORD_BARS = 300;

export default function FocusTimeline({
  duration,
  view,
  onViewChange,
  trim,
  onTrimChange,
  currentTime,
  onSeek,
  words,
  snapToWords,
  onSnapToWordsChange,
  isOverLimit,
}: FocusTimelineProps) {
  const theme = useTheme();
  const trackRef = useRef<HTMLDivElement>(null);
  const overviewRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const overviewDraggingRef = useRef(false);
  const [width, setWidth] = useState(0);
  const [activeDrag, setActiveDrag] = useState<DragMode | null>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);

  const [viewStart, viewEnd] = view;
  const span = Math.max(viewEnd - viewStart, 0.001);
  const clipColor = isOverLimit
    ? theme.palette.error.main
    : theme.palette.primary.main;

  const latest = useRef({ view, duration, onViewChange });
  latest.current = { view, duration, onViewChange };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Native listener: React's onWheel is passive, so it can't stop the modal
  // from scrolling while the user zooms the timeline.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const {
        view: current,
        duration: total,
        onViewChange: change,
      } = latest.current;
      const rect = el.getBoundingClientRect();
      const currentSpan = current[1] - current[0];
      const isPan = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (isPan) {
        const delta = e.shiftKey ? e.deltaY : e.deltaX;
        change(panView(current, (delta / rect.width) * currentSpan, total));
      } else {
        const anchor =
          current[0] + ((e.clientX - rect.left) / rect.width) * currentSpan;
        change(zoomView(current, Math.exp(e.deltaY * 0.0015), anchor, total));
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  const toPercent = (time: number) => ((time - viewStart) / span) * 100;

  const timeFromClientX = (clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return viewStart;
    return clamp(
      viewStart + ((clientX - rect.left) / rect.width) * span,
      0,
      duration,
    );
  };

  const beginDrag = (mode: DragMode) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackRef.current?.setPointerCapture(e.pointerId);
    const time = timeFromClientX(e.clientX);
    dragRef.current = {
      mode,
      offset: time - trim[0],
      startX: e.clientX,
      moved: false,
    };
    setActiveDrag(mode);
    if (mode === "seek") onSeek(time);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const time = timeFromClientX(e.clientX);
    setHoverTime(time);
    const drag = dragRef.current;
    if (!drag) return;
    if (Math.abs(e.clientX - drag.startX) > 3) drag.moved = true;

    const tolerance = (SNAP_TOLERANCE_PX / Math.max(width, 1)) * span;
    const snap = (t: number, edge: "start" | "end") =>
      snapToWords ? snapToWordBoundary(words, t, edge, tolerance) : t;
    const [clipStart, clipEnd] = trim;

    if (drag.mode === "start") {
      onTrimChange(
        clamp(snap(time, "start"), 0, clipEnd - MIN_CLIP_LENGTH),
        clipEnd,
      );
    } else if (drag.mode === "end") {
      onTrimChange(
        clipStart,
        clamp(snap(time, "end"), clipStart + MIN_CLIP_LENGTH, duration),
      );
    } else if (drag.mode === "move" && drag.moved) {
      const length = clipEnd - clipStart;
      const nextStart = clamp(time - drag.offset, 0, duration - length);
      onTrimChange(nextStart, nextStart + length);
    } else if (drag.mode === "seek") {
      onSeek(time);
    }
  };

  const endDrag = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (drag?.mode === "move" && !drag.moved) {
      onSeek(timeFromClientX(e.clientX));
    }
    if (trackRef.current?.hasPointerCapture(e.pointerId)) {
      trackRef.current.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setActiveDrag(null);
  };

  const overviewTime = (clientX: number): number => {
    const rect = overviewRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return clamp(((clientX - rect.left) / rect.width) * duration, 0, duration);
  };

  const handleOverviewDown = (e: React.PointerEvent) => {
    overviewDraggingRef.current = true;
    overviewRef.current?.setPointerCapture(e.pointerId);
    onViewChange(centerView(view, overviewTime(e.clientX), duration));
  };

  const handleOverviewMove = (e: React.PointerEvent) => {
    if (!overviewDraggingRef.current) return;
    onViewChange(centerView(view, overviewTime(e.clientX), duration));
  };

  const handleOverviewUp = (e: React.PointerEvent) => {
    overviewDraggingRef.current = false;
    if (overviewRef.current?.hasPointerCapture(e.pointerId)) {
      overviewRef.current.releasePointerCapture(e.pointerId);
    }
  };

  const ticks = useMemo(() => {
    const step = pickTickStep(span, width);
    const result: number[] = [];
    for (let t = Math.ceil(viewStart / step) * step; t <= viewEnd; t += step) {
      result.push(t);
    }
    return { step, values: result };
  }, [span, width, viewStart, viewEnd]);

  const visibleWords = useMemo(() => {
    if (!words.length || span > MAX_SPAN_FOR_WORD_BARS) return [];
    const first = Math.max(0, findWordIndexAt(words, viewStart));
    const last = findWordIndexAt(words, viewEnd);
    return words.slice(first, last + 1);
  }, [words, span, viewStart, viewEnd]);

  const zoomBy = (factor: number) =>
    onViewChange(
      zoomView(view, factor, clamp(currentTime, viewStart, viewEnd), duration),
    );

  const clipLeft = toPercent(trim[0]);
  const clipRight = toPercent(trim[1]);
  const showDragLabel = activeDrag === "start" || activeDrag === "end";
  const dragLabelTime = activeDrag === "start" ? trim[0] : trim[1];

  if (!duration) return null;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Zoom out">
            <IconButton size="small" onClick={() => zoomBy(1.6)}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom in">
            <IconButton size="small" onClick={() => zoomBy(1 / 1.6)}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fit to clip (F)">
            <IconButton
              size="small"
              onClick={() => onViewChange(fitViewToClip(trim, duration))}
            >
              <CenterFocusStrongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Viewing {formatTimecode(viewStart, false)} –{" "}
            {formatTimecode(viewEnd, false)} · scroll to zoom, shift+scroll to
            pan
          </Typography>
        </Stack>
        <Tooltip
          title={
            words.length
              ? "Handles snap to the nearest word boundary so you never cut mid-word"
              : "Transcript not available"
          }
        >
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                size="small"
                checked={snapToWords && words.length > 0}
                disabled={!words.length}
                onChange={(e) => onSnapToWordsChange(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Snap to words</Typography>}
          />
        </Tooltip>
      </Stack>

      <Box
        ref={trackRef}
        onPointerDown={beginDrag("seek")}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={() => setHoverTime(null)}
        sx={{
          position: "relative",
          height: RULER_HEIGHT + LANE_HEIGHT,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "grey.50",
          border: 1,
          borderColor: "grey.200",
          cursor: "crosshair",
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            height: RULER_HEIGHT,
            borderBottom: 1,
            borderColor: "grey.200",
            bgcolor: "background.paper",
          }}
        >
          {ticks.values.map((t) => (
            <Box
              key={t}
              sx={{
                position: "absolute",
                left: `${toPercent(t)}%`,
                top: 0,
                height: "100%",
                borderLeft: 1,
                borderColor: "grey.300",
                pl: 0.5,
                fontSize: 10,
                lineHeight: `${RULER_HEIGHT}px`,
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {formatTimecode(t, ticks.step < 1)}
            </Box>
          ))}
        </Box>

        {visibleWords.map((w) => (
          <div
            key={`${w.start}-${w.end}`}
            style={{
              position: "absolute",
              left: `${toPercent(w.start)}%`,
              width: `max(1px, ${toPercent(w.end) - toPercent(w.start)}%)`,
              top: RULER_HEIGHT + LANE_HEIGHT / 2 - 7,
              height: 14,
              borderRadius: 2,
              background: speakerColor(w.speaker),
              opacity: 0.45,
              pointerEvents: "none",
            }}
          />
        ))}

        {hoverTime !== null && !activeDrag && (
          <Box
            sx={{
              position: "absolute",
              left: `${toPercent(hoverTime)}%`,
              top: RULER_HEIGHT,
              bottom: 0,
              borderLeft: "1px dashed",
              borderColor: "grey.500",
              pointerEvents: "none",
            }}
          />
        )}

        <Box
          onPointerDown={beginDrag("move")}
          sx={{
            position: "absolute",
            left: `${clipLeft}%`,
            width: `${clipRight - clipLeft}%`,
            top: RULER_HEIGHT,
            bottom: 0,
            bgcolor: alpha(clipColor, 0.14),
            borderTop: `2px solid ${clipColor}`,
            borderBottom: `2px solid ${clipColor}`,
            cursor: activeDrag === "move" ? "grabbing" : "grab",
          }}
        />

        {(["start", "end"] as const).map((edge) => (
          <Box
            key={edge}
            onPointerDown={beginDrag(edge)}
            sx={{
              position: "absolute",
              left: `${edge === "start" ? clipLeft : clipRight}%`,
              transform: edge === "start" ? "translateX(-100%)" : "none",
              top: RULER_HEIGHT,
              bottom: 0,
              width: 14,
              bgcolor: clipColor,
              borderRadius: edge === "start" ? "6px 0 0 6px" : "0 6px 6px 0",
              cursor: "ew-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              "&::before, &::after": {
                content: '""',
                width: "2px",
                height: 16,
                borderRadius: 1,
                bgcolor: "rgba(255,255,255,0.8)",
              },
            }}
          />
        ))}

        <Box
          sx={{
            position: "absolute",
            left: `${toPercent(currentTime)}%`,
            top: 0,
            bottom: 0,
            width: 2,
            ml: "-1px",
            bgcolor: "error.main",
            pointerEvents: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: -4,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: `7px solid ${theme.palette.error.main}`,
            },
          }}
        />

        {showDragLabel && (
          <Box
            sx={{
              position: "absolute",
              left: `${toPercent(dragLabelTime)}%`,
              top: 2,
              transform: "translateX(-50%)",
              px: 0.75,
              borderRadius: 1,
              bgcolor: "grey.900",
              color: "common.white",
              fontSize: 11,
              fontVariantNumeric: "tabular-nums",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {formatTimecode(dragLabelTime)}
          </Box>
        )}
      </Box>

      <Tooltip
        title="Whole episode — click or drag to move the view"
        placement="bottom"
      >
        <Box
          ref={overviewRef}
          onPointerDown={handleOverviewDown}
          onPointerMove={handleOverviewMove}
          onPointerUp={handleOverviewUp}
          onPointerCancel={handleOverviewUp}
          sx={{
            position: "relative",
            mt: 1,
            height: 12,
            borderRadius: 6,
            bgcolor: "grey.200",
            cursor: "pointer",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: `${(trim[0] / duration) * 100}%`,
              width: `max(2px, ${((trim[1] - trim[0]) / duration) * 100}%)`,
              top: 0,
              bottom: 0,
              bgcolor: clipColor,
              borderRadius: 6,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: `${(viewStart / duration) * 100}%`,
              width: `max(6px, ${(span / duration) * 100}%)`,
              top: -2,
              bottom: -2,
              border: 2,
              borderColor: "grey.700",
              borderRadius: 1,
              bgcolor: "rgba(255,255,255,0.35)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: `${(currentTime / duration) * 100}%`,
              top: -2,
              bottom: -2,
              width: 2,
              bgcolor: "error.main",
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
}
